<template>
  <div v-if="loader" class="container-fluid message-container-align">
    <b-spinner
      style="width: 6rem; height: 6rem; color: #003778"
      label="Large Spinner"
    ></b-spinner>
  </div>
  <ErrorModal v-model="otpErrorFlag" :message="otpErrorMessage" />
  <div
    v-if="linkExpiredFlag || invalidIDFlag || errorFlag || unAuthorizedFlag"
    class="container-fluid message-container-align"
  >
    <h2 v-if="unAuthorizedFlag">{{ t("form.unauthorized") }}</h2>
    <h2 v-if="linkExpiredFlag">
      {{ t("form.yourlinkhasexpired") }}
    </h2>
    <h2 v-if="invalidIDFlag">{{ t("form.invalidrequest") }}</h2>
    <h2 v-if="errorFlag && !unAuthorizedFlag">
      {{ t("form.unabletoprocesstherequest") }}
    </h2>
  </div>
  <div
    v-if="
      !loader &&
      !linkExpiredFlag &&
      !invalidIDFlag &&
      !errorFlag &&
      !unAuthorizedFlag
    "
    class="container p-3"
    style="min-height: 363px"
  >
    <p>
      {{ t("otpGenerate.verificationMessage") }}
      <a href="">{{ expiryDays }} {{ t("form.days") }}</a
      >.
    </p>
    <div class="spacer15"></div>
    <div class="verification-box">
      <h5 class="verification-header">
        {{ t("otpGenerate.verificationRequired") }}
      </h5>
      <p class="verification-description">
        {{ t("otpGenerate.receiveOneTimeCode") }}
      </p>
      <b-button pill class="custom-button" @click="validateGenerateOtp">
        {{ t("otpGenerate.sendOneTimeCode") }}
      </b-button>
    </div>
    <div class="spacer30"></div>
    <span id="previousDownloads" class="txtSize15">
      {{ t("download.previousdownloads") }}: {{ downloadCount }}
    </span>
    <div class="spacer5"></div>
    <span id="remainingDownloads" class="txtSize15">
      {{ t("download.remainingdownloads") }}: {{ remainingDownloads }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import CryptoJS from "crypto-js";
import router from "@/router";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { generateChecksum } from "../services/generateChecksum";
import { decryptData } from "../services/decryptData";
import ErrorModal from "./ErrorModal.vue";
const otpErrorMessage = ref("");
const expiryDays = computed(() => userDocument?.value?.ExpiryDays);
const secretKey = process.env.VUE_APP_ENCRYPT_DECRYPT_SECRET_KEY;
const JwtSecretKey = process.env.VUE_APP_JWT_KEY;
const errorFlag = ref(false);
const otpErrorFlag = ref(false);
const unAuthorizedFlag = ref(false);
const store = useStore();
const loader = ref(false);
const { t } = useI18n();
const userDocument = ref<any | null>(null);
const invoiceDetail = ref<any | null>(null);
const downloadCount = computed(() => invoiceDetail?.value?.DownloadCount);
const remainingDownloads = computed(
  () => userDocument?.value?.MaxDownloads - invoiceDetail?.value?.DownloadCount
);
const linkExpiredFlag = ref(false);
const invalidIDFlag = ref(false);
const baseUrl = process.env.VUE_APP_BASE_API_URL;
const route = useRoute();
const userId = ref(route.query.p as string);

const getInvoiceCount = async (token: any) => {
  const checksumString = `${userId.value}`;
  const checksum = generateChecksum(checksumString);
  try {
    const invoiceCountDetail = await axios.get(
      `${baseUrl}/invoice/count/${userId.value}`,
      {
        headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
      }
    );
    const encryptedData = invoiceCountDetail.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = invoiceCountDetail.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(invoiceCountDetail.data);
    const invoiceData = JSON.parse(decryptedText);
    if (
      receivedChecksum === calculatedChecksum &&
      invoiceData.UserId === userId.value
    ) {
      invoiceDetail.value = invoiceData;
      loader.value = false;
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
  } catch (error: any) {
    loader.value = false;
    errorFlag.value = true;
    console.error("Failed to fetch invoice details:", error);
  }
};
const getToken = async (token: any) => {
  token = "";
  try {
    const encryptedDataValue = {
      UserId: userId.value,
      SecretKey: JwtSecretKey,
      timestamp: new Date().toISOString(),
    };
    const dataString = JSON.stringify(encryptedDataValue);
    const encryptedDataString = CryptoJS.AES.encrypt(
      dataString,
      secretKey
    ).toString();
    const tokenResponse = await axios.post(`${baseUrl}/auth/generate-token`, {
      encryptedDataString,
    });
    token = tokenResponse.data.token;
    setTokenInSessionStorage(token, 23);
    return token;
  } catch (error: any) {
    console.error("Failed to generate token:", error);
    if (error.response) {
      if (error.response.status === 404 || error.response.status === 401) {
        unAuthorizedFlag.value = true;
      } else {
        errorFlag.value = true;
        throw new Error("Failed to generate token. Please try again.");
      }
    }
  }
};
const setTokenInSessionStorage = (token: any, expiresIn: number) => {
  const expirationTime = Date.now() + expiresIn * 3600 * 1000;
  sessionStorage.setItem(`token_${userId.value}`, token);
  sessionStorage.setItem(
    `tokenExpiration_${userId.value}`,
    expirationTime.toString()
  );
};

const getTokenFromSessionStorage = () => {
  const token = sessionStorage.getItem(`token_${userId.value}`);
  const expirationTime = parseInt(
    sessionStorage.getItem(`tokenExpiration_${userId.value}`) || "0"
  );

  if (Date.now() > expirationTime) {
    sessionStorage.removeItem(`token_${userId.value}`);
    sessionStorage.removeItem(`tokenExpiration_${userId.value}`);
    return null;
  }

  return token;
};

const fetchUserData = async () => {
  try {
    loader.value = true;
    let token = getTokenFromSessionStorage();

    if (!token) {
      token = await getToken(token);
    }
    const checksumString = `${userId.value}`;
    const checksum = generateChecksum(checksumString);
    try {
      const invoiceResponse = await axios.get(
        `${baseUrl}/invoice/${userId.value}`,
        {
          headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
        }
      );
      const encryptedData = invoiceResponse.data;
      const decryptedText = decryptData(encryptedData);
      const receivedChecksum = invoiceResponse.headers["x-file-checksum"];
      const calculatedChecksum = generateChecksum(invoiceResponse.data);
      const invoiceData = JSON.parse(decryptedText);
      if (
        receivedChecksum === calculatedChecksum &&
        invoiceData.UserId === userId.value
      ) {
        userDocument.value = invoiceData;
      } else {
        loader.value = false;
        errorFlag.value = true;
      }
    } catch (error: any) {
      console.error("Failed to fetch user data", error);
      if (error.response && error.response.status === 404) {
        invalidIDFlag.value = true;
        loader.value = false;
        throw new Error(
          "User ID not found. Please check the ID and try again."
        );
      } else {
        errorFlag.value = true;
        loader.value = false;
        throw new Error("Failed to fetch user data. Please try again later.");
      }
    }
    await getInvoiceCount(token);
    const currentDate = new Date();
    const expiryDate = new Date(userDocument.value.ExpiryDate.value);
    if (userDocument.value?.publicUrl) {
      store.commit("setUserID", userDocument.value.Id);
      if (currentDate > expiryDate) {
        linkExpiredFlag.value = true;
        return;
      } else {
        store.commit("setAuth", true);
        router.push({ name: "PDFDownloadPage", query: { p: userId.value } });
      }

      return;
    }

    if (currentDate > expiryDate) {
      linkExpiredFlag.value = true;
      return;
    }
  } catch (error: any) {
    error.value =
      error.message || "An unexpected error occurred. Please try again later.";
    loader.value = false;
  } finally {
    loader.value = false;
  }
};
onMounted(() => {
  if (store.state.isOtpGenerateAuth) {
    fetchUserData();
  } else {
    loader.value = false;
    invalidIDFlag.value = true;
  }
});
const validateGenerateOtp = async () => {
  const checksumString = `${userId.value}`;
  const checksum = generateChecksum(checksumString);
  let token = getTokenFromSessionStorage();
  if (!token) {
    token = await getToken(token);
  }
  try {
    const otpDetails = await axios.get(`${baseUrl}/generate/${userId.value}`, {
      headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
    });
    console.log("OTP generate api Response:", otpDetails.data);
    const encryptedData = otpDetails.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = otpDetails.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(otpDetails.data);
    const otpData = JSON.parse(decryptedText);
    if (
      receivedChecksum === calculatedChecksum &&
      otpData.UserId === userId.value
    ) {
      if (otpData.Status.toLowerCase() === "success") {
        store.commit("setOtpGenerateAuth", false);
        store.commit("setOtpValidateAuth", true);
        store.commit("setOtpData", {
          method: otpData.OtpMethod,
          prompt: otpData.OtpPrompt,
        });
        loader.value = false;
        router.push({ name: "OtpValidate", query: { p: userId.value } });
      } else {
        otpErrorMessage.value = otpData.OtpData.errorReason;
        loader.value = false;
        otpErrorFlag.value = true;
        return;
      }
    } else {
      loader.value = false;
      errorFlag.value = true;
    }
  } catch (error: any) {
    loader.value = false;
    errorFlag.value = true;
    console.error("Failed to fetch Otp details:", error);
  }
};
</script>

<script lang="ts">
export default {
  name: "OtpGenerateComponent",
};
</script>

<style scoped>
.spacer30 {
  height: 30px;
}
.spacer15 {
  height: 15px;
}
.custom-button {
  color: #fff;
  background-color: #003778;
  border: 2px solid #003778;
  font-size: 15px;
  padding: 10px 32px;
  text-align: center;
  cursor: pointer;
  border-radius: 12px;
  line-height: 1;
  --bs-btn-hover-bg: #003778;
  font-family: Arial, Helvetica, sans-serif;
}

.verification-box {
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.15);
  /* shadow like modal */
  /* text-align: center; */
  max-width: 450px;
  width: 100%;
}

.message-container-align {
  min-height: 363px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.verification-header {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  font-family: Arial, Helvetica, sans-serif;
}

.verification-description {
  font-size: 14px;
  color: #333;
  margin-bottom: 16px;
  font-family: Arial, Helvetica, sans-serif;
}

.container-height {
  min-height: 363px;
}

.custom-modal {
  background: #f7f7f7;
  padding: 20px;
  border-radius: 12px;
}
</style>
